import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card,Box, CardContent, Typography, Grid, CardActionArea, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faHandshake, faMoneyBillWave, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';

const cardData = [
  {
    title: 'All Shops',
    icon: faStore,
    path: '/shop/all-shops',
    levels: [1, 2, 3, 4],
    moduleCode: null,
    description: 'View all registered shops'
  },
  {
    title: 'Commission Management',
    icon: faHandshake,
    path: '/shop/commission-management',
    levels: [1, 2],
    moduleCode: null,
    description: 'Set and edit shop commissions'
  },
  {
    title: 'Reset Commission',
    icon: faMoneyBillWave,
    path: '/shop-report/commission-reset',
    levels: [1, 2],
    moduleCode: null,
    description: 'Reset shop commission history'
  },
  {
    title: 'Rejected Shops',
    icon: faExclamationTriangle,
    path: '/shop/rejected-shops',
    levels: [1, 2],
    moduleCode: null,
    description: 'View and retry failed AceMoney registrations'
  },
];

const ShopManagement = () => {
  const [userLevel, setUserLevel] = useState(null);
  const [userModules, setUserModules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchUserPrivileges = useCallback(async (userId) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/privileges/modules/${userId}`, {
        timeout: 5000
      });
      
      if (response.status === 200 && response.data.modules) {
        setUserModules(response.data.modules);
      } else {
        setError('No modules found for user');
      }
    } catch (error) {
      setError('Failed to load user privileges');
      console.error('Error fetching user privileges:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      setError('Please login to continue');
      setIsLoading(false);
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      setUserLevel(decodedToken.level);

      if (decodedToken.level === 1) {
        // Level 1 gets all cards regardless of levels or modules
        setUserModules(cardData.map(card => card.moduleCode).filter(Boolean));
        setIsLoading(false);
      } else {
        // Fetch privileges for non-level 1 users
        fetchUserPrivileges(decodedToken.userId);
      }
    } catch (error) {
      setError('Invalid authentication token');
      setIsLoading(false);
    }
  }, [fetchUserPrivileges]);

  const hasAccess = useCallback((card) => {
    // Level 1 has access to everything
    if (userLevel === 1) {
      return true;
    }
    
    // Check if user's level matches card's allowed levels
    if (card.levels.includes(userLevel)) {
      return true;
    }
    
    // Check if user has the specific module code (additional access)
    if (card.moduleCode && userModules.includes(card.moduleCode)) {
      return true;
    }
    
    return false;
  }, [userLevel, userModules]);

  const handleCardClick = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  if (isLoading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <Typography color="error">{error}</Typography>
      </Grid>
    );
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
    {/* Place BackButton outside the Grid */}
    <BackButton />
    <br/>  <br/>
    <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{ padding: 3 }}>
      {cardData.map((card) => (
        hasAccess(card) && (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Card
              elevation={3}
              sx={{
                maxWidth: 225,
                backgroundColor: 'white',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  transform: 'translateY(-4px)',
                  '& .MuiTypography-root': { color: 'white' },
                  '& .fa-icon, & svg, & path': { color: '#ffffff !important', fill: '#ffffff !important' },
                },
              }}
            >
              <CardActionArea onClick={() => handleCardClick(card.path)}>
                <CardContent sx={{ textAlign: 'center', padding: 3 }}>
                  <FontAwesomeIcon
                    icon={card.icon}
                    size="5x"
                    className="fa-icon"
                    color="#FFA500"
                  />
                  <Typography 
                    gutterBottom 
                    variant="h5" 
                    component="div" 
                    sx={{ marginTop: 2, fontWeight: 500 }}
                  >
                    {card.title}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        )
      ))}
    </Grid>
    </Box>
  );
};

export default ShopManagement;